import React from "react";
import { ReactComponent as Loader } from "../../../assets/loading_icon.svg";
import { getButtonContent } from "./utils";

export const Button = ({
                           className = "",
                           style,
                           icon,
                           iconOnly,
                           text,
                           iconLeft,
                           iconRight,
                           onClick,
                           pending = false,
                           fullWidth = false,
                           disabled = false,
                           variant = "primary", // "primary" | "secondary" | "link" | "muted"
                           size = "md",         // ✅ "md" | "sm" | "xs"
                           tone = "default",    // ✅ "default" | "danger"
                           type,                // "button" | "submit" | "reset"
                       }) => {
    const content = getButtonContent(icon, text, iconOnly);
    const isDisabled = disabled || pending;

    const baseClasses =
        "inline-flex items-center justify-center " +
        "rounded-[6px] box-border " +
        "text-sm font-medium " +
        "cursor-pointer " +
        "transition-[transform,background-color,color,border-color,opacity] duration-75 " +
        "active:scale-95 disabled:scale-100 disabled:cursor-not-allowed";

    // ✅ Sizes (только для non-link/non-muted; link/muted уже “плоские”)
    const sizeClasses =
        size === "xs"
            ? "px-2.5 py-1.5 text-xs min-w-0"
            : size === "sm"
                ? "px-3 py-2 text-sm min-w-[64px]"
                : "px-5 py-2.5 text-sm min-w-[84px]";

    // BASE by variant (без учёта tone)
    const primaryBase = "bg-[#c06198] text-white disabled:bg-[#818c99]";
    const secondaryBase =
        "bg-transparent border hover:bg-[#c06198]/10 disabled:opacity-60";

    const linkBase =
        "bg-transparent text-[#c06198] p-0 min-w-0 border-0 shadow-none h-auto " +
        "hover:opacity-80 active:scale-100";

    const mutedBase =
        "bg-transparent text-gray-500 p-0 min-w-0 border-0 shadow-none h-auto " +
        "hover:text-gray-700 disabled:opacity-60 active:scale-100";

    // ✅ Tone mapping (default/danger)
    // primary default: pink
    // primary danger: red
    // secondary default: pink outline
    // secondary danger: red outline
    const toneClasses = (() => {
        if (tone !== "danger") {
            if (variant === "secondary") return "text-[#c06198] border-[#c06198]";
            if (variant === "primary") return ""; // уже ок
            return ""; // link/muted не трогаем
        }

        // danger
        if (variant === "primary") {
            return "bg-rose-600 text-white hover:bg-rose-700 disabled:bg-[#818c99]";
        }

        if (variant === "secondary") {
            return "text-rose-600 border-rose-300 hover:bg-rose-50";
        }

        // link/muted: делаем просто красный текст
        if (variant === "link") return "text-rose-600";
        if (variant === "muted") return "text-rose-600 hover:text-rose-700";

        return "";
    })();

    const variantClasses =
        variant === "secondary"
            ? `${secondaryBase} ${toneClasses} ${sizeClasses}`
            : variant === "link"
                ? `${linkBase} ${toneClasses}`
                : variant === "muted"
                    ? `${mutedBase} ${toneClasses}`
                    : `${primaryBase} ${toneClasses} ${sizeClasses}`;

    const widthClasses = fullWidth ? "w-full" : "";
    const computedStyle =
        tone !== "danger"
            ? variant === "secondary"
                ? {
                    background: "var(--brand-secondary-button-background, transparent)",
                    borderColor: "var(--brand-secondary-button-border, #c06198)",
                    color: "var(--brand-secondary-button-text, #c06198)",
                    ...style,
                }
                : variant === "primary"
                    ? {
                        background: "var(--brand-badge-background, #c06198)",
                        color: "var(--brand-badge-text, #ffffff)",
                        ...style,
                    }
                    : variant === "link"
                        ? {
                            color: "var(--brand-accent, #c06198)",
                            ...style,
                        }
                        : style
            : style;

    return (
        <button
            type={type}
            disabled={isDisabled}
            onClick={onClick}
            className={`${baseClasses} ${variantClasses} ${widthClasses} ${className}`}
            style={computedStyle}
        >
            {pending ? (
                <Loader height="18px" width="18px" fill="#fff" stroke="#fff" />
            ) : (
                <>
                    {iconLeft && <span className="mr-2 flex items-center">{iconLeft}</span>}
                    {content}
                    {iconRight && <span className="ml-2 flex items-center">{iconRight}</span>}
                </>
            )}
        </button>
    );
};
